import { useEffect, useState } from 'react';
import { Button } from '@localagent/ui/components/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@localagent/ui/components/dialog';
import { Input } from '@localagent/ui/components/input';
import { Label } from '@localagent/ui/components/label';
import { useCreateCatalogEntry, useUpdateCatalogEntry } from '../../hooks/useProviderManagement';
import { ParamSpecEditor } from './ParamSpecEditor';
import type {
  ModelCatalogEntry,
  ParamTemplate,
  ProviderOwnedCapabilityColumn,
  ProviderType,
  SupportedParamSpec,
  UpdateCatalogEntryParams,
} from '@localagent/sdk';

/**
 * A capability control is TRI-STATE: unset means the provider was silent, and
 * that is not the same as a known false (agents ADR-003). The select therefore
 * has three options, and 'unset' means "leave whatever is stored alone" — the
 * write cannot express NULL, so handing a column back to the provider is the
 * separate Clear override action (persona ADR-019).
 */
type CapabilityChoice = 'unset' | 'true' | 'false';

function toCapabilityChoice(value: boolean | undefined): CapabilityChoice {
  if (value === undefined) return 'unset';
  return value ? 'true' : 'false';
}

function fromCapabilityChoice(choice: CapabilityChoice): boolean | undefined {
  if (choice === 'unset') return undefined;
  return choice === 'true';
}

const CAPABILITY_CONTROLS: ReadonlyArray<{
  column: Extract<ProviderOwnedCapabilityColumn, 'supports_tools' | 'supports_vision'>;
  label: string;
  testid: string;
}> = [
  { column: 'supports_tools', label: 'Tool calling', testid: 'supports-tools' },
  { column: 'supports_vision', label: 'Image input', testid: 'supports-vision' },
];

export type ModelEntryDialogMode = 'add' | 'copy' | 'edit';

const MODE_TITLES: Record<ModelEntryDialogMode, string> = {
  add: 'Add Model',
  copy: 'Copy Model Entry',
  edit: 'Edit Model Entry',
};

export interface ModelEntryDialogProps {
  mode: ModelEntryDialogMode;
  providerType: ProviderType;
  /** Source entry — required for `copy` (prefill + copy_from) and `edit`. */
  source?: ModelCatalogEntry;
  /** Provider-level params — enables fallback hints in edit-mode param editing. */
  providerParams?: Record<string, SupportedParamSpec>;
  open: boolean;
  onClose: () => void;
  onSaved?: (entry: ModelCatalogEntry) => void;
}

export function ModelEntryDialog({
  mode,
  providerType,
  source,
  providerParams,
  open,
  onClose,
  onSaved,
}: ModelEntryDialogProps) {
  const [name, setName] = useState('');
  const [modelId, setModelId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [contextWindow, setContextWindow] = useState('');
  const [maxOutput, setMaxOutput] = useState('');
  const [paramTemplate, setParamTemplate] = useState<ParamTemplate>('standard');
  const [params, setParams] = useState<Record<string, SupportedParamSpec>>({});
  const [supportsTools, setSupportsTools] = useState<CapabilityChoice>('unset');
  const [supportsVision, setSupportsVision] = useState<CapabilityChoice>('unset');
  const [clearOverrides, setClearOverrides] = useState<ProviderOwnedCapabilityColumn[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const createMutation = useCreateCatalogEntry();
  const updateMutation = useUpdateCatalogEntry();
  const isPending = createMutation.isPending || updateMutation.isPending;

  // Reset form state each time the dialog opens (prefilled from source for
  // copy/edit modes).
  useEffect(() => {
    if (!open) return;
    setErrorMessage(null);
    setParamTemplate('standard');
    setClearOverrides([]);
    setSupportsTools(toCapabilityChoice(source?.supports_tools));
    setSupportsVision(toCapabilityChoice(source?.supports_vision));
    if ((mode === 'copy' || mode === 'edit') && source) {
      setName(mode === 'copy' ? `${source.name}-copy` : source.name);
      setModelId(source.model_id);
      setDisplayName(source.display_name ?? '');
      setContextWindow(source.context_window != null ? String(source.context_window) : '');
      setMaxOutput(source.max_output_tokens != null ? String(source.max_output_tokens) : '');
      setParams(source.params);
    } else {
      setName('');
      setModelId('');
      setDisplayName('');
      setContextWindow('');
      setMaxOutput('');
      setParams({});
    }
  }, [open, mode, source]);

  const contextWindowNum = parseInt(contextWindow, 10);
  const maxOutputNum = parseInt(maxOutput, 10);
  const isValid =
    name.trim() !== '' &&
    modelId.trim() !== '' &&
    Number.isFinite(contextWindowNum) && contextWindowNum > 0 &&
    Number.isFinite(maxOutputNum) && maxOutputNum > 0;

  const handleSubmit = () => {
    if (!isValid || isPending) return;
    setErrorMessage(null);

    if (mode === 'edit') {
      if (!source) return;
      // Replace-by-key params payload: current specs plus explicit nulls for
      // entry-level params removed relative to the source entry.
      const paramsPayload: Record<string, SupportedParamSpec | null> = { ...params };
      for (const key of Object.keys(source.params)) {
        if (!(key in params)) paramsPayload[key] = null;
      }
      // Only a CHANGED capability is sent: sending the stored value back would
      // record an override the admin never asked for, and sync would stop
      // refreshing that label forever.
      const toolsChoice = fromCapabilityChoice(supportsTools);
      const visionChoice = fromCapabilityChoice(supportsVision);
      const updateParams: UpdateCatalogEntryParams = {
        name: source.name,
        model_id: modelId.trim(),
        display_name: displayName.trim(),
        context_window: contextWindowNum,
        max_output_tokens: maxOutputNum,
        params: paramsPayload,
        ...(toolsChoice !== undefined && toolsChoice !== source.supports_tools
          ? { supports_tools: toolsChoice }
          : {}),
        ...(visionChoice !== undefined && visionChoice !== source.supports_vision
          ? { supports_vision: visionChoice }
          : {}),
        ...(clearOverrides.length > 0
          ? { clear_capability_overrides: clearOverrides }
          : {}),
      };
      updateMutation.mutate(
        { providerType, params: updateParams },
        {
          onSuccess: (entry) => {
            onSaved?.(entry);
            onClose();
          },
          onError: (err) => setErrorMessage(err.message),
        },
      );
      return;
    }

    createMutation.mutate(
      {
        providerType,
        params: {
          name: name.trim(),
          model_id: modelId.trim(),
          display_name: displayName.trim() || undefined,
          context_window: contextWindowNum,
          max_output_tokens: maxOutputNum,
          ...(mode === 'copy' && source
            ? { copy_from: source.name }
            : { param_template: paramTemplate }),
        },
      },
      {
        onSuccess: (entry) => {
          onSaved?.(entry);
          onClose();
        },
        onError: (err) => setErrorMessage(err.message),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{MODE_TITLES[mode]}</DialogTitle>
          <DialogDescription>
            Model IDs are not validated against the provider — a typo fails at first use.
            {providerType === 'lmstudio' && (
              <> The model must be loaded in LM Studio on the executing device.</>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-2">
          <div>
            <Label htmlFor="provider-entry-name">Entry Name</Label>
            <Input
              id="provider-entry-name"
              data-testid="provider-entry-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="my-model"
              disabled={mode === 'edit'}
            />
            {mode === 'edit' && (
              <p className="mt-1 text-caption text-muted-foreground">Entry names are immutable — agent configs reference them.</p>
            )}
          </div>
          <div>
            <Label htmlFor="provider-model-id">Model ID</Label>
            <Input
              id="provider-model-id"
              data-testid="provider-model-id-input"
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              placeholder="provider-model-identifier"
            />
          </div>
          <div>
            <Label htmlFor="provider-display-name">Display Name</Label>
            <Input
              id="provider-display-name"
              data-testid="provider-display-name-input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Optional display name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="provider-context-window">Context Window</Label>
              <Input
                id="provider-context-window"
                type="number"
                data-testid="provider-context-window-input"
                value={contextWindow}
                onChange={(e) => setContextWindow(e.target.value)}
                placeholder="128000"
              />
            </div>
            <div>
              <Label htmlFor="provider-max-output">Max Output Tokens</Label>
              <Input
                id="provider-max-output"
                type="number"
                data-testid="provider-max-output-input"
                value={maxOutput}
                onChange={(e) => setMaxOutput(e.target.value)}
                placeholder="16000"
              />
            </div>
          </div>
          {mode === 'add' && (
            <div>
              <Label htmlFor="provider-param-template">Parameter Template</Label>
              <select
                id="provider-param-template"
                data-testid="provider-param-template-select"
                value={paramTemplate}
                onChange={(e) => setParamTemplate(e.target.value as ParamTemplate)}
                className="w-full rounded-md bg-muted px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="standard">Standard (temperature, top_p)</option>
                <option value="reasoning">Reasoning (reasoning_effort)</option>
                {/*
                  The three-mode request-level control MiniMax M3 — the production
                  DEFAULT model — needs. It existed in the backend from the start and
                  was unreachable because the API enum was `standard,reasoning`, so
                  the user-facing template ceiling was two, not three.
                */}
                <option value="thinking">Thinking (thinking mode, top_k, temperature, top_p)</option>
              </select>
            </div>
          )}
          {mode === 'edit' && source && (
            <div data-testid="provider-capability-overrides">
              <Label>Provider capabilities</Label>
              <p className="mt-1 text-caption text-muted-foreground">
                The provider reports these and catalog sync keeps them current.
                Setting one records an override, so sync stops changing it; Clear
                override hands the column back to the provider.
              </p>
              {CAPABILITY_CONTROLS.map(({ column, label, testid }) => {
                const choice = column === 'supports_tools' ? supportsTools : supportsVision;
                const setChoice =
                  column === 'supports_tools' ? setSupportsTools : setSupportsVision;
                const overridden = source.capability_overrides?.includes(column) ?? false;
                const pendingClear = clearOverrides.includes(column);
                return (
                  <div key={column} className="mt-2 flex items-center gap-2">
                    <Label htmlFor={`provider-${testid}`} className="w-28 text-caption">
                      {label}
                    </Label>
                    <select
                      id={`provider-${testid}`}
                      data-testid={`provider-${testid}-select`}
                      value={choice}
                      disabled={pendingClear}
                      onChange={(e) => setChoice(e.target.value as CapabilityChoice)}
                      className="rounded-md bg-muted px-2 py-1 text-body focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="unset">Not reported</option>
                      <option value="true">Supported</option>
                      <option value="false">Not supported</option>
                    </select>
                    {overridden && !pendingClear && (
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`provider-${testid}-clear-override`}
                        onClick={() => setClearOverrides((prev) => [...prev, column])}
                      >
                        Clear override
                      </Button>
                    )}
                    {pendingClear && (
                      <span
                        data-testid={`provider-${testid}-clear-pending`}
                        className="text-caption text-muted-foreground"
                      >
                        Provider value restored on save
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {mode === 'edit' && (
            <div>
              <Label>Entry Parameters</Label>
              <ParamSpecEditor
                params={params}
                effectiveParams={source?.effective_params}
                providerParams={providerParams}
                onChange={setParams}
                disabled={isPending}
              />
            </div>
          )}
          {errorMessage && (
            <p className="text-body text-destructive" data-testid="provider-dialog-error">{errorMessage}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="provider-dialog-submit"
            onClick={handleSubmit}
            disabled={!isValid || isPending}
          >
            {isPending ? 'Saving...' : mode === 'edit' ? 'Save' : mode === 'copy' ? 'Copy' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
